import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Brain, CheckCircle2, Loader2 } from "lucide-react";
import { Button } from "../ui/button";
import { cn } from "../../lib/utils";
import useAuth from "../../hooks/useAuth";
import useRetryFetch from "../../hooks/useRetryFetch";
import {
  MmMenu,
  MmObject,
  MmObjectType,
  MmProfile,
  MmRegister,
  MmVisit,
  applyOperator,
  closeVisit,
  createObject,
  getBiography,
  inferProfile,
  openVisit,
  snapshotProfile,
} from "../../api/mentalModels";
import { useMmText } from "../../hooks/useMmText";
import MmProfilePanel from "../mentalModel/MmProfilePanel";
import MmOperatorFlow from "../mentalModel/MmOperatorFlow";

/**
 * Mental Model Studio as an activity pane component (design §10.3, embed
 * phase A). The author binds the engine's slots in the designer: object type,
 * allowed operators, register, and optionally a previous pane's field as the
 * seed statement. The server enforces the operator restriction; guards always
 * win over the author's selection.
 *
 * userData[content.key] = { fiObject, fiVisit, applied: [...], completed }
 */

interface MmStudioConfig {
  intro?: string;
  objectType?: MmObjectType;
  operatorIds?: string[];
  register?: MmRegister;
  seedFieldKey?: string;
  maxOperations?: number;
}

interface SavedState {
  fiObject: string;
  fiVisit?: string;
  applied: Array<{ operatorId: string; summary?: string }>;
  completed?: boolean;
}

interface Props {
  config?: MmStudioConfig;
  userData: Record<string, any>;
  content: { key: string; label?: string };
  onChange: (key: string, value: any) => void;
  readonly?: boolean;
  /** "studio": the full engine, user picks moves freely (operatorIds ignored).
   *  "guided": the author's ordered sequence drives the session. */
  mode?: "studio" | "guided";
}

type Phase = "capture" | "rate" | "session" | "done" | "loading";

const MentalModelStudio: React.FC<Props> = ({
  config = {},
  userData,
  content,
  onChange,
  readonly,
  mode = "studio",
}) => {
  const { t, i18n } = useTranslation();
  const { auth } = useAuth();
  const retryFetch = useRetryFetch();
  const register: MmRegister = config.register || "plain";
  const { tm } = useMmText(register);

  const saved: SavedState | undefined = userData?.[content.key];
  const seedText: string =
    (config.seedFieldKey && typeof userData?.[config.seedFieldKey] === "string"
      ? userData[config.seedFieldKey]
      : "") || "";

  const [phase, setPhase] = useState<Phase>(saved?.fiObject ? "loading" : "capture");
  const [statement, setStatement] = useState(seedText);
  const [object, setObject] = useState<MmObject | null>(null);
  const [visit, setVisit] = useState<MmVisit | null>(null);
  const [menu, setMenu] = useState<MmMenu>({ allowed: [] });
  const [warnings, setWarnings] = useState<string[]>([]);
  const [profileDraft, setProfileDraft] = useState<MmProfile>({});
  const [aiConfidence, setAiConfidence] = useState<Record<string, number> | undefined>();
  const [rerate, setRerate] = useState<{ before: MmProfile; draft: MmProfile } | null>(null);
  const [applied, setApplied] = useState<SavedState["applied"]>(saved?.applied || []);
  const [busy, setBusy] = useState(false);
  const [distress, setDistress] = useState(false);
  // Guided mode: the author's ordered path drives the session step-by-step
  const [route, setRoute] = useState<Array<{ operatorId: string; status: string }>>([]);
  const [requestedOperator, setRequestedOperator] = useState<string | null>(null);

  const language = i18n.language?.split("-")[0] || "en";
  const maxOps = Number(config.maxOperations) || 0;
  // Guided only when the component IS the guided variant AND a path is set;
  // the studio variant always offers the full engine
  const guided = mode === "guided" && (config.operatorIds || []).length > 0;

  /** Next step of the author's path: guard-forced escape first, then the
   * first pending route step the server still allows. */
  const nextGuidedOp = (
    m: MmMenu,
    r: Array<{ operatorId: string; status: string }>
  ): string | null => {
    if (m.forcedFirst && m.allowed.includes(m.forcedFirst)) return m.forcedFirst;
    const step = r.find((s) => s.status === "pending" && m.allowed.includes(s.operatorId));
    return step ? step.operatorId : null;
  };

  const persist = (patch: Partial<SavedState>, base?: SavedState) => {
    const next: SavedState = {
      fiObject: object?._id || saved?.fiObject || "",
      applied,
      ...(base || saved || {}),
      ...patch,
    };
    onChange(content.key, next);
  };

  const startVisit = async (obj: MmObject) => {
    const res = await openVisit(retryFetch, {
      fiObject: obj._id,
      register,
      allowedOperators: guided ? config.operatorIds : undefined,
      context: { surface: "activity" },
    });
    setObject(obj);
    setVisit(res.visit);
    setMenu(res.menu);
    setWarnings(res.warnings);
    setRoute(res.visit.plannedRoute || []);
    setPhase("session");
    // Guided walkthrough: open the first step immediately
    if (guided) {
      setRequestedOperator(nextGuidedOp(res.menu, res.visit.plannedRoute || []));
    }
  };

  // Resume a previously started engagement (pane revisit / process resume)
  useEffect(() => {
    if (readonly) return;
    if (saved?.fiObject && !saved.completed && phase === "loading") {
      getBiography(retryFetch, saved.fiObject)
        .then((res) => startVisit(res.object))
        .catch(() => setPhase("capture"));
    } else if (saved?.completed && phase === "loading") {
      setPhase("done");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ------- render guards ------------------------------------------------------

  if (readonly || (saved?.completed && phase !== "session")) {
    return (
      <div className="rounded-xl border border-border/70 bg-muted/20 p-4 my-2">
        <p className="flex items-center gap-2 text-sm font-medium mb-2">
          <Brain className="w-4 h-4 text-primary/70" />
          {content.label || tm("studio_title", "Mental Models")}
        </p>
        {saved ? (
          <div className="text-xs text-muted-foreground space-y-1">
            {(saved.applied || []).map((a, i) => (
              <p key={i} className="flex items-start gap-1.5">
                <CheckCircle2 className="w-3 h-3 mt-0.5 text-emerald-500 flex-shrink-0" />
                <span>
                  <b className="text-foreground/80">
                    {tm(`op_${a.operatorId}_title`, a.operatorId)}
                  </b>
                  {a.summary && <> — {a.summary}</>}
                </span>
              </p>
            ))}
            {!saved.applied?.length && (
              <p>{tm("embed_nothing_yet", "No moves were made here.")}</p>
            )}
          </div>
        ) : (
          <p className="text-xs text-muted-foreground">
            {tm("embed_nothing_yet", "No moves were made here.")}
          </p>
        )}
      </div>
    );
  }

  if (!auth?.accessToken) {
    return (
      <div className="rounded-xl border border-dashed border-border p-5 my-2 text-center text-sm text-muted-foreground">
        <Brain className="w-6 h-6 mx-auto mb-2 opacity-40" />
        {tm(
          "embed_signin",
          "This step works on a personal thought and keeps it in your workspace — sign in to use it."
        )}
      </div>
    );
  }

  if (distress) {
    return (
      <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 my-2 text-sm text-rose-900">
        {tm(
          "distress_body",
          "What you wrote sounds like a heavy moment. This tool isn't the right support for acute distress — please reach out to someone you trust or a local support line."
        )}
      </div>
    );
  }

  if (phase === "loading") {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground my-3">
        <Loader2 className="w-4 h-4 animate-spin" /> {t("loading", "Loading…")}
      </div>
    );
  }

  // ------- capture ------------------------------------------------------------

  const captureAndRate = async () => {
    // A live seed from a previous pane wins over the (possibly stale) local draft
    const sourceText = seedText.trim().length >= 3 ? seedText.trim() : statement.trim();
    if (sourceText.length < 3) return;
    setBusy(true);
    try {
      const created = await createObject(retryFetch, {
        statement: sourceText,
        objectType: config.objectType || "other",
      });
      setObject(created.object);
      setProfileDraft(created.object.profile);
      persist({ fiObject: created.object._id, applied: [] }, { fiObject: created.object._id, applied: [] });
      try {
        const inferred = await inferProfile(retryFetch, {
          statement: sourceText,
          objectType: config.objectType,
          language,
        });
        if (inferred.distress) {
          setDistress(true);
          return;
        }
        if (inferred.profile) {
          setProfileDraft({ ...created.object.profile, ...inferred.profile });
          setAiConfidence(inferred.confidence);
        }
      } catch {
        /* rating proceeds manually */
      }
      setPhase("rate");
    } catch (e) {
      console.error(e);
    } finally {
      setBusy(false);
    }
  };

  const beginSession = async () => {
    if (!object) return;
    setBusy(true);
    try {
      const savedObj = await snapshotProfile(retryFetch, object._id, {
        profile: profileDraft,
        trigger: "capture_rating",
      });
      await startVisit(savedObj.object);
    } catch (e) {
      console.error(e);
    } finally {
      setBusy(false);
    }
  };

  // ------- session ------------------------------------------------------------

  const handleApply = async (args: { operatorId: string; input?: any; output?: any }) => {
    if (!visit || !object) return;
    setBusy(true);
    try {
      const res = await applyOperator(retryFetch, visit._id, { ...args, proposedBy: "user" });
      if ((res as any).fault) {
        if ((res as any).menu) setMenu((res as any).menu);
        return;
      }
      const out = args.output || {};
      const summary =
        out.chosenName || out.chosenFrame || out.probe ||
        (typeof out.response === "string" ? out.response.slice(0, 100) : undefined);
      const nextApplied = [...applied, { operatorId: args.operatorId, summary }];
      setApplied(nextApplied);
      setObject(res.object);
      setMenu(res.menu);
      setWarnings(res.warnings);
      // Advance the author's path locally (server does the same on the visit)
      setRoute((r) => {
        const idx = r.findIndex(
          (s) => s.operatorId === args.operatorId && s.status === "pending"
        );
        if (idx === -1) return r;
        const next = [...r];
        next[idx] = { ...next[idx], status: "done" };
        return next;
      });
      setRerate({ before: res.delta.before, draft: { ...res.object.profile } });
      persist({ fiVisit: visit._id, applied: nextApplied });
    } catch (e) {
      console.error(e);
    } finally {
      setBusy(false);
    }
  };

  const finish = async () => {
    if (visit) {
      try {
        await closeVisit(retryFetch, visit._id, "completed");
      } catch {
        /* best-effort */
      }
    }
    persist({ completed: true });
    setPhase("done");
  };

  const opsExhausted = maxOps > 0 && applied.length >= maxOps;

  return (
    <div className="rounded-xl border border-border/70 bg-white p-4 my-2 space-y-3">
      <p className="flex items-center gap-2 text-sm font-semibold">
        <Brain className="w-4 h-4 text-primary/70" />
        {content.label || tm("studio_title", "Mental Models")}
      </p>
      {config.intro && phase === "capture" && (
        <p className="text-xs text-muted-foreground leading-relaxed">{config.intro}</p>
      )}

      {phase === "capture" &&
        (seedText.trim().length >= 3 ? (
          // Seeded from a previous pane: no retyping — confirm and begin
          <div className="space-y-2.5">
            <div className="rounded-lg bg-muted/50 border border-border/60 px-3.5 py-3 text-sm italic text-foreground/85">
              “{seedText.trim()}”
            </div>
            <Button size="sm" onClick={captureAndRate} disabled={busy}>
              {busy ? <Loader2 className="w-4 h-4 animate-spin me-2" /> : null}
              {tm("embed_begin", "Work on this thought")}
            </Button>
          </div>
        ) : (
          <div className="space-y-2.5">
            <textarea
              value={statement}
              onChange={(e) => setStatement(e.target.value)}
              rows={3}
              placeholder={tm("capture_placeholder", "e.g. I'm too old to switch fields")}
              className="w-full rounded-lg border border-border px-3 py-2.5 text-sm bg-background"
            />
            <Button size="sm" onClick={captureAndRate} disabled={busy || statement.trim().length < 3}>
              {busy ? <Loader2 className="w-4 h-4 animate-spin me-2" /> : null}
              {tm("capture_next", "Capture it")}
            </Button>
          </div>
        ))}

      {phase === "rate" && object && (
        <div className="space-y-3">
          <p className="text-sm italic text-foreground/80">“{object.statement}”</p>
          <MmProfilePanel
            profile={profileDraft}
            profileSource={object.profileSource}
            confidence={aiConfidence}
            register={register}
            onChange={(attr, value) => setProfileDraft({ ...profileDraft, [attr]: value })}
          />
          <Button size="sm" onClick={beginSession} disabled={busy}>
            {busy ? <Loader2 className="w-4 h-4 animate-spin me-2" /> : null}
            {tm("start_working", "Start working on it")}
          </Button>
        </div>
      )}

      {phase === "session" && object && visit && (
        <div className="space-y-3">
          <p className="text-sm italic text-foreground/80">
            “{object.workingStatement || object.statement}”
          </p>
          {guided && route.length > 0 && (
            <div className="flex flex-wrap items-center gap-1.5">
              {route.map((step, i) => {
                const done = step.status === "done";
                const isCurrent =
                  !done && route.findIndex((s) => s.status !== "done") === i;
                return (
                  <span
                    key={`${step.operatorId}-${i}`}
                    className={cn(
                      "flex items-center gap-1 text-[11px] rounded-full ps-1 pe-2 py-0.5 border",
                      done
                        ? "bg-emerald-50 border-emerald-200 text-emerald-700"
                        : isCurrent
                        ? "bg-primary/[0.07] border-primary/40 text-primary font-medium"
                        : "bg-muted/40 border-border/70 text-muted-foreground"
                    )}
                  >
                    <span
                      className={cn(
                        "flex items-center justify-center h-4 w-4 rounded-full text-[9px] font-semibold tabular-nums",
                        done
                          ? "bg-emerald-500 text-white"
                          : isCurrent
                          ? "bg-primary text-primary-foreground"
                          : "bg-border/80 text-muted-foreground"
                      )}
                    >
                      {done ? <CheckCircle2 className="w-3 h-3" /> : i + 1}
                    </span>
                    {tm(`op_${step.operatorId}_title`, step.operatorId)}
                  </span>
                );
              })}
            </div>
          )}
          {rerate ? (
            <div className="rounded-lg border border-border/70 p-3">
              <p className="text-xs font-medium mb-2.5">
                {tm("rerate_title", "How does it sit with you now?")}
              </p>
              <MmProfilePanel
                profile={rerate.draft}
                register={register}
                onChange={(attr, value) =>
                  setRerate({ ...rerate, draft: { ...rerate.draft, [attr]: value } })
                }
              />
              <Button
                size="sm"
                className="mt-2.5"
                disabled={busy}
                onClick={async () => {
                  setBusy(true);
                  try {
                    const savedObj = await snapshotProfile(retryFetch, object._id, {
                      profile: rerate.draft,
                      trigger: "post_operator",
                      fiVisit: visit._id,
                    });
                    setObject(savedObj.object);
                    setRerate(null);
                    // Guided walkthrough: advance straight to the next step
                    if (guided && !opsExhausted) {
                      setRequestedOperator(nextGuidedOp(menu, route));
                    }
                  } catch (e) {
                    console.error(e);
                  } finally {
                    setBusy(false);
                  }
                }}
              >
                {tm("rerate_save", "Save & continue")}
              </Button>
            </div>
          ) : opsExhausted || (guided && !nextGuidedOp(menu, route) && !requestedOperator) ? (
            <p className="text-xs text-muted-foreground">
              {tm("embed_ops_done", "You've used the moves available in this step.")}
            </p>
          ) : (
            <MmOperatorFlow
              object={object}
              menu={menu}
              warnings={warnings}
              register={register}
              plannedRoute={route}
              applying={busy}
              requestOperator={requestedOperator}
              onRequestConsumed={() => setRequestedOperator(null)}
              hideMenu={guided}
              onApply={handleApply}
              onPark={() => finish()}
              onRetire={() => finish()}
            />
          )}
          <div className="flex justify-end">
            <Button size="sm" variant="outline" onClick={finish} disabled={busy || !!rerate}>
              {tm("embed_finish", "Finish this step")}
            </Button>
          </div>
        </div>
      )}

      {phase === "done" && (
        <div className="text-xs text-muted-foreground space-y-1">
          <p className={cn("flex items-center gap-1.5 text-emerald-700")}>
            <CheckCircle2 className="w-3.5 h-3.5" />
            {tm("embed_done", "Saved to your workspace — you can keep working on it under Mental Models.")}
          </p>
          {applied.map((a, i) => (
            <p key={i} className="ps-5">
              {tm(`op_${a.operatorId}_title`, a.operatorId)}
              {a.summary && <> — {a.summary}</>}
            </p>
          ))}
          {(object?._id || saved?.fiObject) && (
            <button
              onClick={() => {
                sessionStorage.setItem(
                  "mm_open_object",
                  object?._id || saved!.fiObject
                );
                localStorage.setItem("workspace_active_tab", "mental_models");
                window.location.href = "/portal";
              }}
              className="text-primary hover:underline cursor-pointer ps-5"
            >
              {tm("embed_open_workspace", "Open it in your Mental Models →")}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default MentalModelStudio;
